import { memo } from 'react';
import {
  Spider as SpiderVisualization,
  type ScalesResultT,
} from '@mentimeter/visualizations';
import {
  VisualizationUpdater,
  SvgElement,
  useUniqueSelector,
} from '@mentimeter/question-modules-shared';
import type { QuestionRange } from '@mentimeter/http-clients';
import type { VisualizationThemeT } from '@mentimeter/question-modules-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import type { ScalesVisualizationDataHandlerProps } from '../types';

interface SpiderT extends VisualizationThemeT {
  selector: string;
  range: QuestionRange;
  results: ScalesResultT[];
  respondents: number;
  animationMultiplier: 1 | 0;
  lineColor: string;
}

const SpiderComponent = memo<SpiderT>(
  ({
    selector,
    results,
    respondents,
    range,
    animationMultiplier,
    fillColors,
    textColor,
    lineColor,
  }: SpiderT) => {
    const spider = new SpiderVisualization({
      selector: `#${selector}`,
      results,
      respondents,
      range,
      animationMultiplier,
      fillColors,
      textColor,
      lineColor,
    });

    return (
      <SvgElement aria-hidden>
        <VisualizationUpdater updateFn={spider.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

export const SpiderVisualizationDataHandler = ({
  results,
  numberOfRespondents,
  range,
  shouldAnimate,
}: ScalesVisualizationDataHandlerProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const selector = useUniqueSelector();

  const animationMultiplier = shouldAnimate ? 1 : 0;

  if (!results) return null;

  return (
    <SpiderComponent
      selector={selector}
      results={results}
      respondents={numberOfRespondents}
      fillColors={fillColors}
      textColor={textColor}
      lineColor={textColor}
      animationMultiplier={animationMultiplier}
      range={range}
    />
  );
};
